import logger from '../utils/logger';
// smartreader-frontend/src/auth/AuthContext.js
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '../services/auth-service';
import { subscribeToPush } from '../services/push';
import { AUTH_TOKEN_KEY, AUTH_ROLE_KEY, AUTH_USERNAME_KEY } from './storage-keys';

const VALID_ROLES = ['admin', 'operator', 'viewer', 'report-viewer', 'monitor'];

export const AuthContext = createContext({
  token: null,
  role: null,
  username: null,
  login: async () => {},
  logout: () => {},
  hasRole: () => false,
  hasAnyRole: () => false,
});

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null); // Kept for backward compatibility
  const [role, setRole] = useState(null);
  const [username, setUsername] = useState(null);
  const pushSubscribedRef = useRef(false);

  const applyAuth = useCallback(({ role: r, username: u }) => {
    setRole(r);
    setUsername(u || null);
    localStorage.setItem(AUTH_ROLE_KEY, r);
    if (u) {
      localStorage.setItem(AUTH_USERNAME_KEY, u);
    } else {
      localStorage.removeItem(AUTH_USERNAME_KEY);
    }
  }, []);

  const clearAuth = useCallback(() => {
    setToken(null);
    setRole(null);
    setUsername(null);
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(AUTH_ROLE_KEY);
      localStorage.removeItem(AUTH_USERNAME_KEY);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (error) {
      logger.error('[Auth] Logout error:', error);
    } finally {
      clearAuth();
    }
  }, [clearAuth]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.localStorage) return;

    // Use cached role as optimistic value while server validates
    const storedRole = localStorage.getItem(AUTH_ROLE_KEY);
    const storedUsername = localStorage.getItem(AUTH_USERNAME_KEY);
    if (storedRole && VALID_ROLES.includes(storedRole)) {
      if (storedRole === 'operator') {
        window.location.href = '/ttsense/';
        return;
      } else if (storedRole === 'monitor' && !window.location.pathname.startsWith('/reader-logs')) {
        window.location.href = '/reader-logs';
        return;
      }
      setRole(storedRole);
      if (storedUsername) setUsername(storedUsername);
    }

    // Always validate with server regardless of cached role
    authService.fetchCurrentUser()
      .then(({ role: fetchedRole, username: fetchedUsername }) => {
        if (fetchedRole === 'operator') {
          window.location.href = '/ttsense/';
          return;
        } else if (fetchedRole === 'monitor' && !window.location.pathname.startsWith('/reader-logs')) {
          window.location.href = '/reader-logs';
          return;
        }
        if (fetchedRole && VALID_ROLES.includes(fetchedRole)) {
          applyAuth({ role: fetchedRole, username: fetchedUsername });
        } else if (storedRole) {
          // Server says not authenticated but we had a cached role — clear it
          clearAuth();
        }
      })
      .catch(() => { /* network etc., ignore — keep optimistic role if set */ });

    // Clean up legacy token if present
    localStorage.removeItem(AUTH_TOKEN_KEY);
  }, [applyAuth, clearAuth]);

  // Listen for auth:expired events from Axios interceptor
  useEffect(() => {
    const handleExpired = () => {
      logger.warn('[Auth] Session expired — logging out');
      clearAuth();
    };
    window.addEventListener('auth:expired', handleExpired);
    return () => window.removeEventListener('auth:expired', handleExpired);
  }, [clearAuth]);

  // Defer push subscription until user is authenticated
  useEffect(() => {
    if (role && !pushSubscribedRef.current) {
      pushSubscribedRef.current = true;
      subscribeToPush();
    }
  }, [role]);

  const login = async (username, password) => {
    try {
      await authService.login(username, password);
      setToken('cookie');

      const { role: fetchedRole, username: fetchedUsername } = await authService.fetchCurrentUser();

      if (fetchedRole === 'operator') {
        window.location.href = '/ttsense/';
        return;
      } else if (fetchedRole === 'monitor' && !window.location.pathname.startsWith('/reader-logs')) {
        window.location.href = '/reader-logs';
        return;
      }

      if (fetchedRole && VALID_ROLES.includes(fetchedRole)) {
        applyAuth({ role: fetchedRole, username: fetchedUsername });
      }
    } catch (error) {
      clearAuth();
      throw error;
    }
  };

  const hasRole = (r) => role === r;

  const hasAnyRole = (...rolesToCheck) => {
    const list = Array.isArray(rolesToCheck[0]) ? rolesToCheck[0] : rolesToCheck;
    return list.some((r) => r === role);
  };

  return (
    <AuthContext.Provider value={{ token, role, username, login, logout, hasRole, hasAnyRole }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
